/**
 * WMS Dashboard Types
 *
 * Type definitions for the configurable warehouse dashboard system.
 * Supports drag-and-drop widget placement and user customization.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

/**
 * Widget size options
 */
export type WidgetSize = 'small' | 'medium' | 'large' | 'wide' | 'tall' | 'extra-wide';

/**
 * Widget category for organization
 */
export type WidgetCategory =
  | 'orders'        // Order-related widgets
  | 'picking'       // Picking operations
  | 'receiving'     // Goods reception
  | 'inventory'     // Inventory status
  | 'shipping'      // Shipping/dispatch
  | 'kpi'           // Key performance indicators
  | 'activity'      // Real-time activity
  | 'operators'     // Operator stats
  | 'system'        // System info (clock, etc.)
  | 'forecasting'   // Workload forecasting & planning
  | 'gamification'  // Leaderboards, goals, achievements
  | 'alerts'        // Bottlenecks, replenishment alerts
  | 'comparison'    // Period comparison widgets
  | 'compass';      // Spør Compass AI-rapporter

/**
 * Widget refresh interval options (in seconds)
 * Common values: 0 (no refresh), 10, 30, 60, 300
 */
export type RefreshInterval = number;

/**
 * Widget definition - metadata about available widgets
 */
export interface WidgetDefinition {
  id: string;
  name: string;
  description: string;
  category: WidgetCategory;
  defaultSize: WidgetSize;
  allowedSizes: WidgetSize[];
  defaultRefreshInterval: RefreshInterval;
  icon: string; // Lucide icon name
  minWidth: number;  // Grid units
  minHeight: number; // Grid units
  maxWidth?: number;
  maxHeight?: number;
  /** Whether this widget has a backend API endpoint (true = live data available) */
  hasBackend?: boolean;
  /** Whether multiple instances of this widget can be added (default: false) */
  allowMultiple?: boolean;
}

/**
 * Widget instance - a placed widget on the dashboard
 */
export interface WidgetInstance {
  instanceId: string;
  widgetId: string;
  position: {
    x: number;
    y: number;
  };
  size: {
    width: number;
    height: number;
  };
  settings: WidgetSettings;
}

/**
 * Widget-specific settings
 */
export interface WidgetSettings {
  refreshInterval: RefreshInterval;
  title?: string; // Override default title
  showHeader: boolean;
  customConfig?: Record<string, unknown>;
}

/**
 * Dashboard layout configuration
 */
export interface DashboardLayout {
  id: string;
  name: string;
  widgets: WidgetInstance[];
  gridColumns: number;
  gridRows: number;
  createdAt: string;
  updatedAt: string;
  isDefault: boolean;
}

/**
 * Dashboard state
 */
export interface DashboardState {
  currentLayout: DashboardLayout;
  availableLayouts: DashboardLayout[];
  isEditMode: boolean;
  selectedWidgetId: string | null;
  isFullscreen: boolean;
}

/**
 * Mock data types for widgets
 */

export interface OrderSummary {
  pending: number;
  inProgress: number;
  completed: number;
  delayed: number;
  todayTotal: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
}

export interface PickingStats {
  activePickers: number;
  ordersInPicking: number;
  linesPickedToday: number;
  avgPickTime: number; // seconds per line
  pickRate: number; // lines per hour
  efficiency: number; // percentage
}

export interface ReceivingStats {
  expectedDeliveries: number;
  receivedToday: number;
  pendingPutaway: number;
  avgReceiveTime: number; // minutes per delivery
}

export interface InventoryAlert {
  id: string;
  itemNo: string;
  itemName: string;
  alertType: 'low_stock' | 'out_of_stock' | 'overstock' | 'expiring';
  currentQty: number;
  minQty?: number;
  maxQty?: number;
  location?: string;
  severity: 'warning' | 'critical';
}

export interface ShippingStats {
  readyToShip: number;
  shippedToday: number;
  pendingLabels: number;
  avgShipTime: number; // minutes
  onTimeRate: number; // percentage
}

export interface OperatorActivity {
  operatorId: string;
  operatorName: string;
  currentTask: string;
  taskType: 'picking' | 'receiving' | 'putaway' | 'transfer' | 'idle';
  performance: number; // percentage vs average
  linesProcessed: number;
}

export interface ActivityEvent {
  id: string;
  timestamp: Date;
  type: 'pick' | 'receive' | 'putaway' | 'transfer' | 'adjustment';
  operator: string;
  location: string;
  itemNo: string;
  qty: number;
  description: string;
}

export interface KPIData {
  name: string;
  value: number;
  target: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
}

export interface WarehouseCapacity {
  totalLocations: number;
  usedLocations: number;
  utilizationPercent: number;
  zones: Array<{
    name: string;
    total: number;
    used: number;
    percent: number;
  }>;
}

export interface HourlyActivity {
  hour: string;
  picks: number;
  receives: number;
  shipments: number;
}

/**
 * Default grid configuration
 */
export const DEFAULT_GRID_CONFIG = {
  columns: 12,
  rows: 8,
  gap: 16,
  padding: 16,
};

/**
 * Widget size to grid units mapping
 */
export const WIDGET_SIZE_MAP: Record<WidgetSize, { width: number; height: number }> = {
  small: { width: 2, height: 2 },
  medium: { width: 3, height: 2 },
  large: { width: 4, height: 3 },
  wide: { width: 6, height: 2 },
  tall: { width: 3, height: 4 },
  'extra-wide': { width: 8, height: 2 },
};

/**
 * Category display info
 */
export const CATEGORY_INFO: Record<WidgetCategory, { label: string; color: string }> = {
  orders: { label: 'Ordrer', color: 'blue' },
  picking: { label: 'Plukking', color: 'green' },
  receiving: { label: 'Mottak', color: 'purple' },
  inventory: { label: 'Lagerbeholdning', color: 'orange' },
  shipping: { label: 'Utsending', color: 'cyan' },
  kpi: { label: 'KPI', color: 'pink' },
  activity: { label: 'Aktivitet', color: 'yellow' },
  operators: { label: 'Operatører', color: 'indigo' },
  system: { label: 'System', color: 'gray' },
  forecasting: { label: 'Prognose', color: 'teal' },
  gamification: { label: 'Gamification', color: 'amber' },
  alerts: { label: 'Varsler', color: 'red' },
  comparison: { label: 'Sammenligning', color: 'violet' },
  compass: { label: 'Compass', color: 'blue' },
};
